import { parsearFecha, parsearMonto, montosEnLinea, limpiarConcepto, round2, fechaDesdePartes } from "./helpers";

/**
 * Plantilla GENÉRICA robusta (fallback para bancos sin parser dedicado).
 *
 * 
 *  - NO se ancla en la fecha (cada banco la pone distinto: "06 ENE", "01/03/2025",
 *    o sólo el día "05"); se ancla en los MONTOS + el SALDO corriente.
 *  - Un renglón con >= 2 montos = un movimiento: el último monto es el saldo y el
 *    penúltimo es el importe. El tipo (cargo/abono) se deduce del signo del delta
 *    del saldo respecto al movimiento anterior. Esto es independiente del formato
 *    y de cómo el banco nombre sus columnas (Cargos/Abonos, Depósitos/Retiros...).
 *  - Se acota a la sección de movimientos (entre el encabezado de columnas y la
 *    fila de "Total") y se excluyen líneas de resumen para no contaminar la cadena.
 *  - La fecha es "best effort": fecha completa al inicio, o sólo día + el mes/año
 *    del periodo; si no se encuentra, se hereda la del movimiento anterior.
 *
 * Limitación: si el banco NO muestra saldo corriente, no se puede deducir el tipo
 * (queda null) y se empata sólo por monto + fecha.
 */

const MESES_NOMBRE = {
  enero: 0, febrero: 1, marzo: 2, abril: 3, mayo: 4, junio: 5,
  julio: 6, agosto: 7, septiembre: 8, setiembre: 8, octubre: 9,
  noviembre: 10, diciembre: 11,
};

// Encabezado de la tabla de movimientos: trae "concepto/descripción" y "saldo".
const RE_HEADER = /(concepto|descrip)/i;
// Fin de la sección de movimientos.
const RE_TOTAL = /^\s*total/i;
// Líneas de resumen/encabezado que nunca son movimientos (red de seguridad).
const RE_EXCLUIR = /saldo\s+(inicial|anterior|final|m[íi]nimo|promedio)|comisi|portafolio|requerido|^\s*[+\-=]|gat\b/i;
// Día al inicio del renglón (sólo número), p.ej. Banregio "05 ...".
const RE_DIA_INICIO = /^\s*(\d{1,2})\b/;
// Fecha completa al inicio (varios formatos comunes).
const FORMATOS_FECHA = ["DD/MM/YYYY", "DD/MM/YY", "DD-MM-YYYY", "DD-MM-YY", "DD-MMM-YY", "DD-MMM-YYYY", "DD MMM", "DD/MMM"];
const RE_FECHA_INICIO =
  /^\s*(\d{1,2}[/\- ](?:\d{1,2}|[A-Za-zÁÉÍÓÚáéíóú]{3,})(?:[/-]\d{2,4})?)/;

function esHeader(linea) {
  return RE_HEADER.test(linea) && /saldo/i.test(linea);
}

// Mes/año del periodo desde el encabezado, p.ej. "del 01 al 31 de AGOSTO 2021".
function detectarPeriodo(lineas) {
  for (const l of lineas) {
    const m = l
      .toLowerCase()
      .match(/(enero|febrero|marzo|abril|mayo|junio|julio|agosto|septiembre|setiembre|octubre|noviembre|diciembre)\s+(?:de\s+)?(\d{4})/);
    if (m) return { mes: MESES_NOMBRE[m[1]], anio: parseInt(m[2], 10) };
  }
  return null;
}

// Saldo inicial / anterior para arrancar la cadena de saldos.
function detectarSaldoInicial(lineas) {
  for (let i = 0; i < lineas.length; i++) {
    if (/saldo\s+(inicial|anterior)/i.test(lineas[i])) {
      const enLinea = montosEnLinea(lineas[i]);
      if (enLinea.length) return enLinea[0];
      // El valor puede venir en la(s) línea(s) siguiente(s).
      for (let j = i + 1; j <= i + 2 && j < lineas.length; j++) {
        const ms = montosEnLinea(lineas[j]);
        if (ms.length) return ms[0];
      }
    }
  }
  return null;
}

// Fecha de un renglón: completa, o sólo día + periodo, o null.
function fechaDeLinea(linea, periodo, anioRef) {
  const mFull = linea.match(RE_FECHA_INICIO);
  if (mFull) {
    const f = parsearFecha(mFull[1].trim(), anioRef, FORMATOS_FECHA);
    if (f) return f;
  }
  if (periodo) {
    const mDia = linea.match(RE_DIA_INICIO);
    if (mDia) {
      const dia = parseInt(mDia[1], 10);
      if (dia >= 1 && dia <= 31) {
        const f = parsearFecha(
          `${dia}/${periodo.mes + 1}/${periodo.anio}`,
          anioRef,
          ["D/M/YYYY"]
        );
        if (f) return f;
      }
    }
  }
  return null;
}

const plantillaGenerica = {
  id: "generica",
  nombre: "Genérica (auto)",
  detectar: () => false, // sólo se usa como respaldo
  // Encabezado best-effort: intenta varios formatos de período y de saldo final.
  extraerEncabezado(lineas) {
    const t = (lineas || []).join("\n");
    let fechaInicial = null;
    let fechaFinal = null;

    let m = t.match(/del\s+(\d{1,2})\s+al\s+(\d{1,2})\s+de\s+([a-záéíóú]+)\s+(\d{4})/i);
    if (m) {
      fechaInicial = fechaDesdePartes(m[1], m[3], m[4]);
      fechaFinal = fechaDesdePartes(m[2], m[3], m[4]);
    }
    if (!fechaInicial) {
      m = t.match(/del\s+(\d{1,2})\/([a-záéíóú]+)\/(\d{2,4})\s+al\s+(\d{1,2})\/([a-záéíóú]+)\/(\d{2,4})/i);
      if (m) {
        fechaInicial = fechaDesdePartes(m[1], m[2], m[3]);
        fechaFinal = fechaDesdePartes(m[4], m[5], m[6]);
      }
    }
    if (!fechaInicial) {
      m = t.match(/(\d{1,2})-([a-záéíóú]{3,})-(\d{2,4})\s*\/\s*(\d{1,2})-([a-záéíóú]{3,})-(\d{2,4})/i);
      if (m) {
        fechaInicial = fechaDesdePartes(m[1], m[2], m[3]);
        fechaFinal = fechaDesdePartes(m[4], m[5], m[6]);
      }
    }
    if (!fechaInicial) {
      m = t.match(/(\d{1,2}\/\d{1,2}\/\d{2,4})\s+al\s+(\d{1,2}\/\d{1,2}\/\d{2,4})/i);
      if (m) {
        fechaInicial = parsearFecha(m[1], null, ["DD/MM/YYYY", "DD/MM/YY"]);
        fechaFinal = parsearFecha(m[2], null, ["DD/MM/YYYY", "DD/MM/YY"]);
      }
    }

    const sf =
      t.match(/saldo\s+(?:final|actual|al\s+corte)\b[^$\d]*\$?\s*(-?\d{1,3}(?:,\d{3})*\.\d{2})/i);

    return {
      fechaInicial,
      fechaFinal,
      saldoFinal: sf ? parsearMonto(sf[1]) : null,
    };
  },
  parsear(lineas, anioRef) {
    const arr = lineas || [];

    // 1) Acotar a la sección de movimientos: del primer encabezado a la fila "Total".
    let ini = arr.findIndex(esHeader);
    if (ini === -1) ini = -1; // sin encabezado: se recorre todo
    let fin = arr.length;
    for (let i = ini + 1; i < arr.length; i++) {
      if (RE_TOTAL.test(arr[i])) { fin = i; break; }
    }
    const seccion = arr.slice(ini + 1, fin);

    const periodo = detectarPeriodo(arr);
    let prevSaldo = detectarSaldoInicial(arr);

    // 2) ¿El estado de cuenta trae saldo corriente? (mayoría de renglones con >=2 montos)
    let con2 = 0, con1 = 0;
    for (const l of seccion) {
      if (esHeader(l) || RE_EXCLUIR.test(l)) continue;
      const c = montosEnLinea(l).length;
      if (c >= 2) con2++;
      else if (c === 1) con1++;
    }
    const haySaldo = con2 >= con1;

    // 3) Extraer movimientos.
    const movimientos = [];
    let ultimaFecha = null;
    for (const linea of seccion) {
      if (esHeader(linea) || RE_EXCLUIR.test(linea)) continue;
      const ms = montosEnLinea(linea);
      if (haySaldo ? ms.length < 2 : ms.length < 1) continue;

      const fecha = fechaDeLinea(linea, periodo, anioRef) || ultimaFecha;
      if (fecha) ultimaFecha = fecha;

      let monto, tipo = null, saldo;
      if (haySaldo) {
        saldo = ms[ms.length - 1];
        monto = Math.abs(ms[ms.length - 2]);
        if (prevSaldo != null) {
          tipo = round2(saldo - prevSaldo) >= 0 ? "cargo" : "abono";
        }
        prevSaldo = saldo;
      } else {
        // Sin saldo corriente: el importe es el primer monto; no se puede deducir tipo.
        monto = Math.abs(ms[0]);
      }

      movimientos.push({
        fecha,
        monto: round2(monto),
        tipo,
        concepto: limpiarConcepto(linea),
        saldo,
        raw: linea,
      });
    }

    return movimientos;
  },
};

export default plantillaGenerica;
